import { useMemo } from "react";

import { useTranslations } from "next-intl";

import { ConvertedPrices, OriginalPrice } from "@/types/pricing.types";
import { Amount, AmountVariant, RangeAmount } from "@/ui/Common/Amount";
import { cn } from "@/utils/cn";
import { CurrencyChanger } from "@/widgets/CurrencyChanger";

interface PlanDetailPricingProps {
  pricing?: {
    originalPrice?: OriginalPrice;
    originalPricePerSqM?: OriginalPrice;
    originalPriceMin?: OriginalPrice;
    originalPriceMax?: OriginalPrice;
    originalPricePerSqMMin?: OriginalPrice;
    originalPricePerSqMMax?: OriginalPrice;
    convertedPrices?: ConvertedPrices;
    convertedPricesMin?: ConvertedPrices;
    convertedPricesMax?: ConvertedPrices;
    convertedPricesPerSqM?: ConvertedPrices;
    convertedPricesPerSqMMin?: ConvertedPrices;
    convertedPricesPerSqMMax?: ConvertedPrices;
  };
  showVariants?: AmountVariant[];
  className?: string;
}

// Helper to determine if range should be displayed using original prices
const hasValidRange = (
  minPrice?: OriginalPrice,
  maxPrice?: OriginalPrice,
): boolean => {
  if (!minPrice || !maxPrice) return false;
  const minVal = Number(minPrice.amount);
  const maxVal = Number(maxPrice.amount);

  // If values missing, zero or equal – treat as single price
  if (isNaN(minVal) || isNaN(maxVal)) {
    return false;
  }
  if (minVal === 0 && maxVal === 0) {
    return false;
  }
  return true;
};

export function PlanDetailPricing({
  pricing,
  showVariants = [AmountVariant.FULL],
  className,
}: PlanDetailPricingProps) {
  const t = useTranslations();

  // Memoized calculations for range validation
  const hasFullPriceRange = useMemo(
    () => hasValidRange(pricing?.originalPriceMin, pricing?.originalPriceMax),
    [pricing?.originalPriceMin, pricing?.originalPriceMax],
  );

  const hasPerSqMPriceRange = useMemo(
    () =>
      hasValidRange(
        pricing?.originalPricePerSqMMin,
        pricing?.originalPricePerSqMMax,
      ),
    [pricing?.originalPricePerSqMMin, pricing?.originalPricePerSqMMax],
  );

  return (
    <div className={cn("flex flex-col gap-y-4 items-start", className)}>
      <div
        className={
          "flex items-center flex-wrap gap-y-4 justify-between md:justify-start w-full md:w-auto gap-x-12 xl:gap-x-16"
        }
      >
        {showVariants.includes(AmountVariant.FULL) && (
          <>
            {hasFullPriceRange ? (
              <RangeAmount
                className="text-[2.1rem] font-bold w-full"
                convertedPricesMin={pricing!.convertedPricesMin}
                convertedPricesMax={pricing!.convertedPricesMax}
                variant={AmountVariant.FULL}
              />
            ) : (
              <Amount
                className="text-[2.1rem] font-bold"
                convertedPrices={pricing?.convertedPrices}
                variant={AmountVariant.FULL}
              />
            )}
          </>
        )}

        {showVariants.includes(AmountVariant.PER_SQUARE) && (
          <>
            {hasPerSqMPriceRange && (
              <RangeAmount
                className="text-[2.1rem] font-medium w-full"
                convertedPricesMin={pricing!.convertedPricesPerSqMMin}
                convertedPricesMax={pricing!.convertedPricesPerSqMMax}
                variant={AmountVariant.PER_SQUARE}
              />
            )}

            {!hasPerSqMPriceRange && pricing?.convertedPricesPerSqM && (
              <Amount
                className="text-[2.1rem] font-medium"
                convertedPrices={pricing.convertedPricesPerSqM}
                variant={AmountVariant.PER_SQUARE}
              />
            )}
          </>
        )}

        {showVariants.includes(AmountVariant.PER_MONTH) && (
          <>
            <Amount
              className="text-[2.1rem] font-bold"
              convertedPrices={pricing?.convertedPrices}
              variant={AmountVariant.PER_MONTH}
            />
          </>
        )}

        <CurrencyChanger
          classNames={{
            arrow: "text-red",
          }}
        />
      </div>
    </div>
  );
}
